/**
 * Design tokens — the single source of truth for colour, spacing, type and
 * radius. They reach the stylesheets as CSS custom properties (`--ci-*`) set
 * on the document root by `applyTheme`, so a tenant's accent can be swapped at
 * runtime without rebuilding any CSS.
 *
 * The palette is dark-first (navy/black base, electric cyan accent, white
 * type). Every text colour is asserted to meet WCAG AA against every surface
 * it sits on (`__tests__/tokens.test.ts`), and a tenant accent that would break
 * that is refused rather than applied.
 */

export const DEFAULT_ACCENT = '#22d3ee'

export const palette = {
  bg: '#060a13',
  bgRaised: '#0a1020',
  surface: '#0e1628',
  surfaceRaised: '#131d33',
  surfaceHover: '#18243d',
  border: '#1e2b45',
  borderStrong: '#2c3d5e',
  text: '#f3f7fc',
  textMuted: '#a6b5ca',
  textSubtle: '#8496b0',
  accent: DEFAULT_ACCENT,
  positive: '#34d399',
  negative: '#f87171',
  warning: '#fbbf24',
  info: '#60a5fa',
  critical: '#fb7185',
  chartPrevious: '#7c8ba3',
  /** A mark that is no series of its own — the Not tracked bar — in the same quiet grey as the previous period. */
  chartNeutral: '#7c8ba3',
  /**
   * Two categorical series slots for charts that show two different things at once
   * (spend against attributed revenue): fixed hues, independent of the tenant accent so
   * a brand colour can never make the two series the same. Blue then orange, the dataviz
   * reference palette's dark steps, validated as a pair against `surface` (CVD ΔE 26.8,
   * normal-vision ΔE 31.8, both ≥ 3:1).
   */
  chartSeries1: '#3987e5',
  chartSeries2: '#d95926',
} as const

export type PaletteKey = keyof typeof palette

/** Text colours and the surfaces they are placed on; each pair must meet AA. */
export const TEXT_ON_SURFACE: ReadonlyArray<readonly [PaletteKey, PaletteKey]> = (
  ['text', 'textMuted', 'textSubtle', 'accent', 'positive', 'negative', 'warning', 'info', 'critical'] as const
).flatMap((foreground) =>
  (['bg', 'bgRaised', 'surface', 'surfaceRaised', 'surfaceHover'] as const).map(
    (background) => [foreground, background] as const,
  ),
)

export const space = {
  '1': '4px',
  '2': '8px',
  '3': '12px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '8': '32px',
  '10': '40px',
} as const

export const radius = { sm: '6px', md: '10px', lg: '14px', pill: '999px' } as const

export const fontSize = {
  xs: '11px',
  sm: '12px',
  md: '13px',
  base: '14px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '30px',
} as const

export const font = {
  sans: "'Inter', 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
  mono: "'JetBrains Mono', 'SFMono-Regular', Menlo, Consolas, monospace",
} as const

/** WCAG 2.2 minimum contrast for body text. */
export const AA_TEXT = 4.5

export function hexToRgb(hex: string): [number, number, number] | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match?.[1]) return null
  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : match[1]
  return [0, 2, 4].map((offset) => parseInt(digits.slice(offset, offset + 2), 16)) as [
    number,
    number,
    number,
  ]
}

function channel(value: number): number {
  const scaled = value / 255
  return scaled <= 0.04045 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) throw new Error(`not a hex colour: ${hex}`)
  const [r, g, b] = rgb.map(channel) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastRatio(foreground: string, background: string): number {
  const a = relativeLuminance(foreground)
  const b = relativeLuminance(background)
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

/**
 * A tenant's brand accent, if it is usable on the dark theme; otherwise the
 * default cyan. The accent is used for link text and focus rings, so it has to
 * read at AA on every surface — a navy brand colour would vanish, and a
 * branding setting must never be able to make the product inaccessible.
 */
export function resolveAccent(candidate?: string | null): string {
  if (!candidate) return DEFAULT_ACCENT
  const rgb = hexToRgb(candidate)
  if (!rgb) return DEFAULT_ACCENT
  const normalised = `#${rgb.map((value) => value.toString(16).padStart(2, '0')).join('')}`
  const readable = (['bg', 'surface', 'surfaceRaised', 'surfaceHover'] as const).every(
    (background) => contrastRatio(normalised, palette[background]) >= AA_TEXT,
  )
  return readable ? normalised : DEFAULT_ACCENT
}

function kebab(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
}

export interface ThemeOptions {
  accent?: string | null
}

export function cssVariables(options: ThemeOptions = {}): Record<string, string> {
  const accent = resolveAccent(options.accent)
  const [r, g, b] = hexToRgb(accent) ?? [34, 211, 238]
  const variables: Record<string, string> = {}

  for (const [key, value] of Object.entries(palette)) variables[`--ci-${kebab(key)}`] = value
  variables['--ci-accent'] = accent
  variables['--ci-accent-soft'] = `rgb(${r} ${g} ${b} / 0.14)`
  variables['--ci-accent-border'] = `rgb(${r} ${g} ${b} / 0.45)`
  variables['--ci-accent-glow'] = `rgb(${r} ${g} ${b} / 0.28)`
  variables['--ci-accent-contrast'] =
    contrastRatio(accent, palette.bg) >= contrastRatio(accent, '#ffffff') ? palette.bg : '#ffffff'

  for (const [key, value] of Object.entries(space)) variables[`--ci-space-${key}`] = value
  for (const [key, value] of Object.entries(radius)) variables[`--ci-radius-${key}`] = value
  for (const [key, value] of Object.entries(fontSize)) variables[`--ci-text-${key}`] = value
  for (const [key, value] of Object.entries(font)) variables[`--ci-font-${key}`] = value

  return variables
}

export function applyTheme(target: HTMLElement, options: ThemeOptions = {}): void {
  for (const [name, value] of Object.entries(cssVariables(options))) {
    target.style.setProperty(name, value)
  }
}
